import { format, parseISO } from 'date-fns';
import { AlertCircle } from 'lucide-react';

import {
  useCareTeam,
  useClinicalState,
  useProtocol,
} from '@/lib/api/hooks';
import { ClinicianAvatar } from '@/features/clinic/components/ClinicianAvatar';
import { PageHeader } from '@/components/app-shell';
import type { PrescriptionPurchase, TitrationStep } from '@/types/api';
import { PrescriptionBillingNotice } from './PrescriptionBillingNotice';
import { ShopTreatmentsCard } from './ShopTreatmentsCard';


/**
 * ProtocolsScreen / "Your Stack" — mock-matched.
 *
 * Sections, top to bottom:
 *   1. Sunken header band: YOUR STACK + ▶ weekly note caption + goal pills
 *   2. Dark cycle-progress bar (CYCLE n | WEEK k → WEEK total)
 *   3. CARE PLAN — bordered 2x2 grid (Dose / Frequency / Schedule / Cycle)
 *   4. MEDICATIONS IN THIS PROTOCOL — one card per active prescription
 *
 * Sections render only when their backing data is present so the empty
 * state stays clean for a brand-new patient.
 */
export function ProtocolsScreen() {
  const { data: clinical, isLoading: clinicalLoading } = useClinicalState();
  const { data: protocol, isLoading: protocolLoading, error: protocolError } = useProtocol();

  const prescriptions = (clinical?.prescribed_medications ?? []).filter(
    (p) =>
      Boolean(p.healthie_medication_name) &&
      // Hide unmapped orphans — a Healthie medication that maps to no product
      // AND has no order (e.g. one entered with a misspelled name). There's
      // nothing actionable on it for the patient and it renders an empty,
      // confusing card; mapping gaps are handled in the admin dash, not here.
      (Boolean(p.product_id) || Boolean(p.pharmacy_order_id)),
  );
  const hasStack = prescriptions.length > 0;
  const protocolMissing = (protocolError as { status?: number } | undefined)?.status === 404;

  // Hold the skeleton until both calls settle: with no prescriptions, the
  // populated layout would otherwise flash (header + notice) before the
  // protocol 404 swaps in the empty state.
  if ((clinicalLoading && !clinical) || (!hasStack && protocolLoading && !protocol)) {
    return (
      <div className="flex flex-col gap-20 animate-pulse">
        <div className="h-[3rem] w-[24rem] max-w-full bg-[var(--surface-sunken)]" />
        <div className="h-[14rem] bg-[var(--surface-sunken)]" />
        <div className="h-[18rem] bg-[var(--surface-sunken)]" />
      </div>
    );
  }

  if (!hasStack && (protocolMissing || (!protocol && !protocolLoading))) {
    return (
      <div className="flex flex-col">
        <PageHeader
          title="Your stack"
          description="Your prescriptions and protocol will appear here once your clinician sets them up."
        />
        <div className="px-15 md:px-40 py-25 flex flex-col gap-15">
          <div className="border border-[#ECECEC] px-15 py-30">
            <AlertCircle size={20} strokeWidth={1.5} className="text-[#7C7C7C]" />
            <div className="font-mono text-12 uppercase text-[#7C7C7C] mt-15">
              No active prescriptions yet
            </div>
            <p className="font-body text-13 text-[#7C7C7C] mt-10 max-w-[44ch] leading-135">
              Once your clinician prescribes a medication, it will appear here.
            </p>
          </div>
          <ShopTreatmentsCard />
        </div>
      </div>
    );
  }

  const tags = protocol?.progress?.tags ?? [];
  const weeklyNote = protocol
    ? protocol.progress.weekly_note ||
      `Cycle ${protocol.progress.cycle_number} · Week ${protocol.progress.current_week}`
    : undefined;

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Your stack"
        description={
          weeklyNote ? (
            <>
              <span className="text-[#5E5E5E]">This week:</span> {weeklyNote}
            </>
          ) : undefined
        }
      />
      {tags.length > 0 && (
        <div className="bg-[var(--surface-sunken)] px-15 md:px-40 pb-20 -mt-10 flex gap-10 flex-wrap">
          {tags.map((t) => (
            <span
              key={t}
              className="font-body text-13 text-black border border-[#DBDBDB] bg-white px-12 py-6"
            >
              {t}
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-col px-15 md:px-40 py-25 gap-25 max-w-[65rem] mx-auto w-full">
      <PrescriptionBillingNotice />

      {protocol && (
        <CycleStrip
          cycleNumber={protocol.progress.cycle_number}
          currentWeek={protocol.progress.current_week}
          totalWeeks={protocol.progress.total_weeks}
        />
      )}

      {protocol && protocol.medications.length > 0 && (
        <section className="flex flex-col gap-10">
          <h2 className="type-label-md text-ink-secondary px-5">CARE PLAN</h2>
          <FieldGrid
            fields={[
              { label: 'DOSE', value: protocol.medications[0].dose },
              { label: 'FREQUENCY', value: protocol.medications[0].frequency },
              { label: 'SCHEDULE', value: protocol.medications[0].schedule },
              {
                label: 'CYCLE',
                value: `${protocol.cycle_weeks_on} weeks on · ${protocol.cycle_weeks_off} weeks off`,
              },
            ]}
          />
        </section>
      )}

      {hasStack && (
        <Medications
          prescriptions={prescriptions}
          awaitingConsult={Boolean(clinical?.awaiting_consult)}
        />
      )}
      </div>
    </div>
  );
}

function CycleStrip({
  cycleNumber,
  currentWeek,
  totalWeeks,
}: {
  cycleNumber: number;
  currentWeek: number;
  totalWeeks: number;
}) {
  const segments = Array.from({ length: totalWeeks }, (_, i) => i < currentWeek);
  return (
    <div className="bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] px-15 py-15 flex flex-col gap-10">
      <div className="flex items-center justify-between type-label-sm">
        <span>
          CYCLE {cycleNumber} <span className="opacity-50 mx-5">|</span> WEEK {currentWeek}
        </span>
        <span className="opacity-60">WEEK {totalWeeks}</span>
      </div>
      <div className="flex items-center gap-3" aria-hidden>
        {segments.map((done, i) => (
          <span
            key={i}
            className={
              'flex-1 h-3 ' + (done ? 'bg-[var(--ink-on-inverse)]' : 'bg-[var(--ink-on-inverse)] opacity-25')
            }
          />
        ))}
      </div>
    </div>
  );
}

function Medications({
  prescriptions,
  awaitingConsult,
}: {
  prescriptions: PrescriptionPurchase[];
  awaitingConsult: boolean;
}) {
  return (
    <section data-tour="stack-overview" className="flex flex-col gap-15">
      <div className="px-5">
        <h2 className="type-label-sm text-ink-secondary">
          MEDICATIONS IN THIS PROTOCOL
        </h2>
      </div>
      <div className="flex flex-col gap-15">
        {prescriptions.map((p) => (
          <PrescriptionCard
            key={p.id}
            p={p}
            awaitingConsult={awaitingConsult}
          />
        ))}
      </div>
    </section>
  );
}

function FieldGrid({
  fields,
}: {
  fields: Array<{ label: string; value: string }>;
}) {
  return (
    <dl className="grid grid-cols-2 border-line-subtle">
      {fields.map((f, i) => {
        // On an odd count the final cell spans both columns so the grid
        // doesn't leave a lopsided gap (e.g. DOSE / FREQUENCY / ROUTE).
        const isLastOdd = i === fields.length - 1 && fields.length % 2 === 1;
        return (
          <div
            key={f.label}
            className={
              (isLastOdd ? 'col-span-2 ' : '') +
              (!isLastOdd && i % 2 === 0 ? 'border-r border-line-subtle' : '')
            }
          >
            <dt className="type-label-xs text-ink-muted bg-sunken px-15 py-10">{f.label}</dt>
            <dd className="type-body-md mt-5 break-words px-15 py-10 border-0">{f.value || '—'}</dd>
          </div>
        );
      })}
    </dl>
  );
}

function PrescriptionCard({
  p,
  awaitingConsult,
}: {
  p: PrescriptionPurchase;
  awaitingConsult: boolean;
}) {
  const orderStatus = p.pharmacy_order_status;
  const erxFlow = Boolean(orderStatus);
  // While the consult gate is unsatisfied — booked or not — surface
  // "AWAITING CONSULT" so the patient knows the live visit is what's
  // blocking, not the clinician's keyboard. Flips back to "AWAITING RX"
  // only after the visit occurs.
  const consultGated =
    orderStatus === 'awaiting_prescription' && awaitingConsult;
  // The reviewer is the patient's own prescriber (care-team physician);
  // with several prescribers it is no longer always the same clinician.
  const isAwaitingRx = orderStatus === 'awaiting_prescription';
  const careTeam = useCareTeam(isAwaitingRx);
  const reviewer = careTeam.data?.providers.find((provider) => provider.role === 'physician');
  const statusLabel = erxFlow
    ? consultGated
      ? 'AWAITING CONSULT'
      : orderStatus === 'awaiting_prescription'
      ? 'AWAITING RX'
      : orderStatus === 'awaiting_pharmacy_routing'
        ? 'AWAITING ROUTING'
        : orderStatus === 'pending_review' || orderStatus === 'pending_approval'
          ? 'PENDING REVIEW'
          : orderStatus === 'processing'
            ? 'PROCESSING'
            : orderStatus === 'shipped'
              ? 'SHIPPED'
              : orderStatus === 'delivered'
                ? 'DELIVERED'
                : orderStatus === 'cancelled'
                  ? 'CANCELLED'
                  : (orderStatus ?? '').toUpperCase()
    : p.purchase_state === 'paid'
      ? 'ACTIVE'
      : p.purchase_state === 'unpaid'
        ? 'PRESCRIBED'
        : null;
  const meta = (p.metadata ?? {}) as Record<string, unknown>;
  const healthie = (meta.healthie ?? {}) as Record<string, unknown>;
  // Until the clinician writes the actual Rx, the eRx fields are empty.
  // Show a status word rather than a bare em-dash so the patient
  // understands these will populate after the relevant step. Sync-consult
  // patients are blocked on the live visit (not Hinds's keyboard), so
  // use "Pending consult" for them.
  const isAwaiting = erxFlow && orderStatus === 'awaiting_prescription';
  const placeholder = consultGated
    ? 'Pending consult'
    : isAwaiting
      ? 'Pending review'
      : '—';
  // Prefer OUR templated, pharmacy-exact directions (resolved server-side
  // from the order/catalog); fall back to the raw — usually empty — Healthie
  // medication node.
  const dir = p.directions ?? null;
  const directions = dir?.sig ?? String(healthie.directions ?? '');
  const titration = dir?.titration ?? [];
  const hasTitration = titration.length > 0;
  const dose =
    dir?.dose ?? (healthie.dosage ? String(healthie.dosage) : placeholder);
  const frequency =
    dir?.frequency ??
    (healthie.frequency
      ? String(healthie.frequency)
      : isAwaiting
        ? placeholder
        : 'As directed');
  const route =
    dir?.route ?? (healthie.route ? String(healthie.route) : placeholder);

  return (
    <article className="border border-line-subtle">
      <header className="px-15 py-12 bg-sunken border-b border-line-subtle">
        <div className="flex items-start justify-between gap-10 flex-wrap">
          <div className="flex flex-col gap-2 min-w-0">
            <h3 className="type-title-sm text-ink">
              {p.display_name ?? p.healthie_medication_name ?? 'Prescription'}
            </h3>
            {p.strength_label && (
              <span className="type-body-sm text-ink-muted">
                {p.strength_label}
              </span>
            )}
          </div>
          {statusLabel && (
            <span className="type-label-xs text-ink-secondary bg-[var(--surface-raised)] px-8 py-3 whitespace-nowrap">
              {statusLabel}
            </span>
          )}
        </div>
        {/* Pre-Rx (awaiting_prescription) the server-resolved directions are
            the CATALOG template, not the clinician's actual script — showing
            them (or a "Prescribed" date) before the eRx exists reads as if
            the prescription were already written. Hold everything back
            behind an Awaiting Rx state until the script lands. */}
        {!isAwaiting && !hasTitration && directions && (
          <p className="type-body-sm text-ink-secondary mt-8 whitespace-pre-line">
            {directions}
          </p>
        )}
        {/* For titrating meds the per-week breakdown lives in the table below;
            show the general directions (route, rotate sites, etc.) up here. */}
        {!isAwaiting && hasTitration && dir?.note && (
          <p className="type-body-sm text-ink-secondary mt-8 whitespace-pre-line">
            {dir.note}
          </p>
        )}
      </header>
      {isAwaiting ? (
        <div className="px-15 py-20 flex flex-col gap-15">
          {/* Reviewing-clinician tag: the patient's assigned prescriber
              from the care team (photo when we have one, else initials). */}
          <div className="flex items-center gap-10 bg-sunken border border-line-subtle px-12 py-10 w-fit">
            <ClinicianAvatar
              name={reviewer?.display_name ?? 'Your clinician'}
              src={reviewer?.avatar_url ?? undefined}
              size={32}
            />
            <div className="flex flex-col">
              <span className="type-label-sm text-ink">
                {reviewer?.display_name ?? 'Your clinician'}
              </span>
              <span className="type-body-sm text-ink-muted">
                {consultGated
                  ? 'Will review your information after your consult'
                  : 'Is reviewing your information'}
              </span>
            </div>
          </div>
          <p className="type-body-sm text-ink-secondary m-0 max-w-prose">
            {consultGated
              ? 'Your personalized dosing will appear here after your consult, once your clinician writes your prescription.'
              : 'Your personalized dosing will appear here once your clinician reviews your intake and writes your prescription.'}
          </p>
        </div>
      ) : hasTitration ? (
        <TitrationTable steps={titration} route={route} />
      ) : (
        <FieldGrid
          fields={[
            { label: 'DOSE', value: dose },
            { label: 'FREQUENCY', value: frequency },
            { label: 'ROUTE', value: route },
          ]}
        />
      )}
      <footer className="px-15 py-10 border-t border-line-subtle bg-sunken">
        <span className="type-label-sm text-ink-muted">
          {isAwaiting
            ? `Purchased ${format(parseISO(p.prescribed_at), 'MMM d, yyyy')}`
            : `Prescribed ${format(parseISO(p.prescribed_at), 'MMM d, yyyy')}`}
        </span>
      </footer>
    </article>
  );
}

// Week-by-week dosing table for titrating prescriptions (e.g. the NAD
// ramp-up). Driven entirely by the server-resolved titration steps, so
// changing the schedule is a catalog edit — no code change.
function TitrationTable({
  steps,
  route,
}: {
  steps: TitrationStep[];
  route: string;
}) {
  return (
    <div className="px-15 py-12">
      <div className="type-label-xs text-ink-muted mb-4">
        TITRATION SCHEDULE
      </div>
      {/* No surrounding box — just row dividers, with the rows flush-left so
          "Week X" aligns with the section labels above/below. */}
      <div className="flex flex-col divide-y divide-line-subtle">
        {steps.map((s, i) => (
          <div
            key={i}
            className="grid grid-cols-[5.5rem_1fr_auto] gap-10 py-10 items-baseline"
          >
            <span className="type-label-sm text-ink whitespace-nowrap">
              {/week/i.test(s.weeks) ? s.weeks : `Week ${s.weeks}`}
            </span>
            <span className="type-body-sm text-ink-secondary">
              {s.units && s.dose
                ? `${s.units} (${s.dose})`
                : s.units || s.dose || '—'}
            </span>
            <span className="type-body-sm text-ink-secondary text-right whitespace-nowrap">
              {s.frequency ?? ''}
            </span>
          </div>
        ))}
      </div>
      {route && route !== '—' && (
        <div className="type-label-sm text-ink-muted mt-10">
          ROUTE: <span className="text-ink-secondary">{route}</span>
        </div>
      )}
    </div>
  );
}
